<template>
  <v-container>
    <v-form v-model="formValid">
      <v-autocomplete
        v-model="selectedSchool"
        :items="schools"
        item-text="school"
        item-value="dbname"
        label="Select a School"
        :loading="loadingSchools"
        clearable
      />

      <v-text-field
        v-model="studentName"
        label="Enter your student name (full name or surname)"
        :disabled="!selectedSchool"
        hint="Enter the full name (e.g., John Smith) or just the surname"
        persistent-hint
      />

      <v-btn
        :disabled="!selectedSchool || !studentName"
        @click="findMatch"
        :loading="validating"
      >
        Submit
      </v-btn>
    </v-form>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'

export default {
  data: () => ({
    formValid: true,
    selectedSchool: null,
    studentName: '',
    schools: [],
    loadingSchools: false,
    validating: false,
    submitting: false
  }),
  computed: {
    ...mapState(['Dbasename', 'userId', 'schoolId'])
  },
  methods: {
    async fetchSchools () {
      this.loadingSchools = true
      try {
        const response = await this.$http.get('/students/getAllSchools')
        this.schools = response.data || []

        if (this.schools.length === 0) {
          this.showMessage('No schools found in the system. Please contact support.', 'warning', 'No Schools')
        }
      } catch (error) {
        console.error('Error fetching schools:', error)
        this.showMessage('Unable to load schools. Please refresh the page.', 'error', 'Error')
        this.schools = []
      } finally {
        this.loadingSchools = false
      }
    },
    async findMatch () {
      const trimmedName = this.studentName ? this.studentName.trim() : ''

      if (trimmedName.length < 2) {
        this.showMessage('Please enter at least 2 characters for the student name', 'warning', 'Invalid Input')
        return
      }

      if (!this.selectedSchool) {
        this.showMessage('Please select a school first', 'warning', 'Invalid Input')
        return
      }

      this.validating = true

      try {
        const response = await this.$http.post('/students/matchKid', {
          studentName: trimmedName,
          otherSchoolDbasename: this.selectedSchool,
          currentSchoolDbasename: this.Dbasename,
          currentCaregiverId: this.userId
        })

        const { matched, displayName, schoolName } = response.data

        if (matched === 'none') {
          this.formValid = false
          this.showMessage(
            'No student found with that exact name, or your contact details do not match the school records.\n\n' +
            'Please check:\n' +
            '• The spelling is exactly correct\n' +
            '• You are using the full name or surname\n' +
            '• Your name matches the contact details registered at the school\n\n' +
            'If you believe this is an error, please contact the school administration.',
            'error',
            'No Match Found'
          )
        } else if (matched === 'multiple') {
          this.formValid = false
          this.showMessage(
            'More than one student matches that name. This is unusual and may indicate twins or duplicate records.\n\n' +
            'Please contact the school administration for assistance in linking the correct student.',
            'warning',
            'Multiple Matches Found'
          )
        } else if (matched === 'one') {
          this.formValid = true
          await this.showConfirmation(displayName, schoolName)
        }
      } catch (error) {
        console.error('Error during validation:', error)
        const msg = (error.response && error.response.data && error.response.data.error) || 'An error occurred while validating. Please try again.'
        this.showMessage(msg, 'error', 'Error')
      } finally {
        this.validating = false
      }
    },
    async showConfirmation (displayName, schoolName) {
      const result = await this.$swal({
        icon: 'info',
        title: 'Confirm Information',
        html: `
          <div style="text-align: left; padding: 10px;">
            <p style="margin: 10px 0;"><strong>Student name:</strong><br>${displayName}</p>
            <p style="margin: 10px 0;"><strong>School:</strong><br>${schoolName}</p>
          </div>
          <div style="margin-top: 20px; padding: 10px; background-color: #f5f5f5; border-radius: 4px;">
            <p style="margin: 0;"><strong>Is this information correct?</strong></p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Yes, this is correct',
        cancelButtonText: 'No, cancel',
        confirmButtonColor: '#4CAF50',
        cancelButtonColor: '#f44336'
      })

      if (result.isConfirmed) {
        await this.confirmLink()
      } else {
        this.showMessage(
          'Linking cancelled. Please check the student name and try again, or contact the school if the information displayed was incorrect.',
          'info',
          'Cancelled'
        )
      }
    },
    async confirmLink () {
      if (this.submitting) {
        return
      }

      this.submitting = true

      try {
        await this.$http.post('/students/saveKid', {
          studentName: this.studentName.trim(),
          otherSchoolDbasename: this.selectedSchool,
          currentSchoolDbasename: this.Dbasename,
          currentCaregiverId: this.userId
        })

        await this.showMessage(
          'Student has been successfully linked to your account!',
          'success',
          'Success'
        )

        setTimeout(() => {
          location.reload()
        }, 1000)
      } catch (error) {
        console.error('Error submitting student details:', error)
        const msg = (error.response && error.response.data && error.response.data.error) || 'An error occurred while linking the student. Please try again.'
        this.showMessage(msg, 'error', 'Error')
      } finally {
        this.submitting = false
      }
    },
    async showMessage (message, icon = 'success', title = 'Success') {
      await this.$swal({
        icon,
        title,
        text: message
      })
    }
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      try {
        vm.$store.commit('toggle_dialog', true)
        vm.$store.commit('loading', true)

        await vm.fetchSchools()

        vm.$store.commit('loading', false)
      } catch (error) {
        console.error('Error during route enter:', error)
        vm.$store.commit('loading', false)
        vm.showMessage('Error loading page. Please refresh.', 'error', 'Error')
      }
    })
  }
}
</script>
